import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, MapPin, Calendar, CreditCard, Share2, Download, Search, User, CheckCircle } from 'lucide-react';
import NearULogo from '../components/NearULogo';
import { QRCodeCanvas } from 'qrcode.react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51SVCSo382aFGgdDTM8fb8m063DuKfpCwaW8SSpAZ3KBkKMkGeEVhUkMUeA22P7hQwe04Bxs5eqRx0NhhAjdS87Qm001YK7K9gw');

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: { color: '#E6D7C3', fontFamily: '"Helvetica Neue", Helvetica, sans-serif', fontSmoothing: 'antialiased', fontSize: '16px', '::placeholder': { color: '#8A7355' } },
    invalid: { color: '#fa755a', iconColor: '#fa755a' },
  },
};

function PaymentForm({ orderData, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/romance/purchase`, orderData);
      const result = await stripe.confirmCardPayment(data.client_secret, { payment_method: { card: elements.getElement(CardElement) } });
      if (result.error) {
        setError(result.error.message);
      } else {
        const confirmRes = await axios.post(`${API}/romance/confirm-payment/${data.transaction_id}`);
        onSuccess(confirmRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '24px' }}>
      <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      {error && <div style={{ padding: '12px', background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)', borderRadius: '8px', color: '#fca5a5', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}
      <button type="submit" disabled={!stripe || processing} style={{ width: '100%', padding: '16px', background: processing ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: processing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <CreditCard size={20} />
        {processing ? 'Processing...' : `Pay $${orderData.totalPrice}`}
      </button>
    </form>
  );
}

const SendRomancePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: select items, 2: delivery details, 3: AI confirmation, 4: payment, 5: success
  const [options, setOptions] = useState({ flowers: [], chocolates: [], location: 'Miami' });
  const [formData, setFormData] = useState({
    selectedFlower: null,
    selectedChocolate: null,
    deliveryDate: '',
    recipientType: 'custom', // 'member' or 'custom'
    recipientMemberId: '',
    recipientName: '',
    recipientAddress: '',
    message: '',
    subtotal: 0,
    platformFee: 0,
    totalPrice: 0
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [aiResponse, setAiResponse] = useState('');
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/romance/options?location=Miami`);
      setOptions(data);
    } catch (error) {
      console.error('Error fetching options:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = (flowerPrice = 0, chocolatePrice = 0) => {
    const subtotal = flowerPrice + chocolatePrice;
    const platformFee = Math.round(subtotal * 0.10 * 100) / 100;
    const total = Math.round((subtotal + platformFee) * 100) / 100;
    return { subtotal, platformFee, total };
  };

  const selectFlower = (flower) => {
    const { subtotal, platformFee, total } = calculateTotal(flower.price, formData.selectedChocolate?.price || 0);
    setFormData({ ...formData, selectedFlower: flower, subtotal, platformFee, totalPrice: total });
  };

  const selectChocolate = (chocolate) => {
    const { subtotal, platformFee, total } = calculateTotal(formData.selectedFlower?.price || 0, chocolate.price);
    setFormData({ ...formData, selectedChocolate: chocolate, subtotal, platformFee, totalPrice: total });
  };

  const searchMembers = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/romance/members/search?query=${encodeURIComponent(query)}`);
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching members:', error);
    }
  };

  const selectMember = async (member) => {
    try {
      const { data } = await axios.get(`${API}/romance/members/${member.id}/address`);
      setFormData({
        ...formData,
        recipientType: 'member',
        recipientMemberId: member.id,
        recipientName: data.name,
        recipientAddress: data.address || ''
      });
      setMemberSearch(member.name);
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching member address:', error);
      alert('Could not fetch member address');
    }
  };

  const handleAIConfirmation = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/romance/ai-delivery-confirmation`, {
        flowerName: formData.selectedFlower?.name,
        vendor: formData.selectedFlower?.vendor,
        recipientName: formData.recipientName,
        deliveryAddress: formData.recipientAddress,
        deliveryDate: formData.deliveryDate,
        totalPrice: formData.totalPrice,
        basePrice: formData.subtotal,
        platformFee: formData.platformFee,
        message: formData.message
      });
      setAiResponse(data.message);
      setTimeout(() => setConfirmationSent(true), 1500);
      setStep(4);
    } catch (error) {
      alert('AI Confirmation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (data) => {
    setQrData(data);
    setStep(5);
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('qr-code-canvas');
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `flower-gift-qr-${qrData.unique_code}.png`;
    link.href = url;
    link.click();
  };

  const shareQRCode = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Luxury Flower Gift', text: `I sent you beautiful flowers! ${formData.message}`, url: qrData.qr_url });
      } catch (err) { console.error('Error sharing:', err); }
    } else {
      navigator.clipboard.writeText(qrData.qr_url);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div style={{ paddingBottom: '80px', minHeight: '100vh', background: 'linear-gradient(180deg, #0D0D0D 0%, #2A1B0E 100%)' }}>
      <div className="page-header">
        <button onClick={() => step === 1 ? navigate('/gifts') : setStep(step - 1)} style={{ background: 'none', border: 'none', color: '#E6D7C3', cursor: 'pointer', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <div style={{ marginBottom: '12px' }}><NearULogo size="small" showText={true} /></div>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {step === 5 ? 'GIFT SENT!' : 'LUXURY FLOWERS & CHOCOLATES'}
        </h1>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Progress */}
        {step < 5 && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', justifyContent: 'center' }}>
            {[1, 2, 3, 4].map(s => (
              <div key={s} style={{ width: '40px', height: '4px', background: s <= step ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.2)', borderRadius: '2px' }} />
            ))}
          </div>
        )}

        {/* Step 1: Select Items */}
        {step === 1 && (
          <div>
            <p style={{ color: '#A57C33', marginBottom: '24px', fontSize: '14px' }}>
              <MapPin size={16} style={{ display: 'inline', marginRight: '6px' }} />
              Top luxury vendors in {options.location}
            </p>
            
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Select Flowers</h2>
            {loading ? (
              <p style={{ color: '#A57C33' }}>Loading options...</p>
            ) : (
              <>
                <div style={{ display: 'grid', gap: '16px', marginBottom: '32px' }}>
                  {options.flowers.map(flower => (
                    <div key={flower.id} onClick={() => selectFlower(flower)} style={{ background: formData.selectedFlower?.id === flower.id ? 'rgba(215, 181, 109, 0.15)' : 'rgba(230, 215, 195, 0.05)', border: `1px solid ${formData.selectedFlower?.id === flower.id ? '#D7B56D' : '#A57C33'}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s ease' }}>
                      {flower.image && (
                        <img src={flower.image} alt={flower.name} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      )}
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '12px', color: '#A57C33', marginBottom: '4px' }}>{flower.vendor}</p>
                            <h3 style={{ fontSize: '18px', color: '#E6D7C3', fontWeight: 'bold', marginBottom: '8px' }}>{flower.name}</h3>
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                              {flower.category && (
                                <span style={{ fontSize: '11px', padding: '3px 8px', background: 'rgba(215, 181, 109, 0.2)', color: '#D7B56D', borderRadius: '4px', textTransform: 'uppercase' }}>{flower.category}</span>
                              )}
                              {flower.occasion && (
                                <span style={{ fontSize: '11px', padding: '3px 8px', background: 'rgba(200, 168, 130, 0.2)', color: '#C8A882', borderRadius: '4px' }}>
                                  {flower.occasion === 'Love' ? '❤️ Love' : '🤝 Friendship'}
                                </span>
                              )}
                            </div>
                          </div>
                          <span style={{ fontSize: '18px', color: '#D7B56D', fontWeight: 'bold', marginLeft: '12px' }}>${flower.price}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#8A7355', marginBottom: '8px' }}>{flower.description}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ color: '#D7B56D', fontSize: '14px' }}>★</span>
                          <span style={{ color: '#A57C33', fontSize: '13px' }}>{flower.rating}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Add Premium Chocolates (Optional)</h2>
                <div style={{ display: 'grid', gap: '16px', marginBottom: '32px' }}>
                  {options.chocolates.map(chocolate => (
                    <div key={chocolate.id} onClick={() => selectChocolate(chocolate)} style={{ background: formData.selectedChocolate?.id === chocolate.id ? 'rgba(215, 181, 109, 0.15)' : 'rgba(230, 215, 195, 0.05)', border: `1px solid ${formData.selectedChocolate?.id === chocolate.id ? '#D7B56D' : '#A57C33'}`, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s ease' }}>
                      {chocolate.image && (
                        <img src={chocolate.image} alt={chocolate.name} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                      )}
                      <div style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '4px' }}>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '12px', color: '#A57C33', marginBottom: '4px' }}>{chocolate.vendor}</p>
                            <h3 style={{ fontSize: '18px', color: '#E6D7C3', fontWeight: 'bold', marginBottom: '4px' }}>{chocolate.name}</h3>
                          </div>
                          <span style={{ fontSize: '18px', color: '#D7B56D', fontWeight: 'bold', marginLeft: '12px' }}>${chocolate.price}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: '#8A7355', marginBottom: '8px' }}>{chocolate.description}</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ color: '#D7B56D', fontSize: '14px' }}>★</span>
                          <span style={{ color: '#A57C33', fontSize: '13px' }}>{chocolate.rating}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.totalPrice > 0 && (
                  <div style={{ background: 'rgba(215, 181, 109, 0.1)', border: '1px solid #D7B56D', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                    {formData.selectedFlower && <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '4px' }}>✓ {formData.selectedFlower.name}</p>}
                    {formData.selectedChocolate && <p style={{ color: '#E6D7C3', fontSize: '14px', marginBottom: '12px' }}>✓ {formData.selectedChocolate.name}</p>}
                    <div style={{ borderTop: '1px solid rgba(215, 181, 109, 0.3)', paddingTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ color: '#A57C33', fontSize: '14px' }}>Subtotal:</span>
                        <span style={{ color: '#E6D7C3', fontSize: '14px' }}>${formData.subtotal}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#A57C33', fontSize: '14px' }}>Near U Fee (10%):</span>
                        <span style={{ color: '#E6D7C3', fontSize: '14px' }}>${formData.platformFee}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(215, 181, 109, 0.3)' }}>
                        <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>Total:</span>
                        <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '20px' }}>${formData.totalPrice}</span>
                      </div>
                    </div>
                  </div>
                )}

                <button onClick={() => setStep(2)} disabled={!formData.selectedFlower} style={{ width: '100%', padding: '16px', background: !formData.selectedFlower ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: !formData.selectedFlower ? 'not-allowed' : 'pointer' }}>Continue to Delivery Details</button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Delivery Details */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Delivery Details</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '12px', fontSize: '14px' }}>Send To</label>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'member'})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'member' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'member' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <User size={16} /> NearU Member
                </button>
                <button type="button" onClick={() => setFormData({...formData, recipientType: 'custom', recipientMemberId: '', recipientName: '', recipientAddress: ''})} style={{ flex: 1, padding: '12px', background: formData.recipientType === 'custom' ? 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100())' : 'rgba(230, 215, 195, 0.05)', color: formData.recipientType === 'custom' ? '#2C1B13' : '#E6D7C3', border: '1px solid #A57C33', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Custom Address</button>
              </div>

              {formData.recipientType === 'member' ? (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '14px', color: '#A57C33' }} />
                    <input 
                      type="text" 
                      placeholder="Search member by name or email" 
                      value={memberSearch} 
                      onChange={(e) => { setMemberSearch(e.target.value); searchMembers(e.target.value); }}
                      style={{ width: '100%', padding: '12px 12px 12px 40px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} 
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '48px', left: 0, right: 0, background: '#1a1410', border: '1px solid #A57C33', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                      {searchResults.map(member => (
                        <div key={member.id} onClick={() => selectMember(member)} style={{ padding: '12px', borderBottom: '1px solid rgba(165, 124, 51, 0.2)', cursor: 'pointer', transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(215, 181, 109, 0.1)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <p style={{ color: '#E6D7C3', fontSize: '14px', fontWeight: 'bold', marginBottom: '2px' }}>{member.name}</p>
                          <p style={{ color: '#A57C33', fontSize: '12px' }}>{member.email}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <input 
                  type="text" 
                  placeholder="Recipient name" 
                  value={formData.recipientName} 
                  onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                  style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} 
                />
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Delivery Address</label>
              <textarea 
                placeholder="Enter full delivery address" 
                value={formData.recipientAddress} 
                onChange={(e) => setFormData({...formData, recipientAddress: e.target.value})} 
                rows="3" 
                style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none', resize: 'vertical' }} 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Delivery Date</label>
              <input 
                type="date" 
                value={formData.deliveryDate} 
                onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})} 
                style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none' }} 
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: '#A57C33', marginBottom: '8px', fontSize: '14px' }}>Personal Message</label>
              <textarea 
                placeholder="Add a heartfelt message..." 
                value={formData.message} 
                onChange={(e) => setFormData({...formData, message: e.target.value})} 
                rows="3" 
                style={{ width: '100%', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '8px', color: '#E6D7C3', fontSize: '16px', outline: 'none', resize: 'vertical' }} 
              />
            </div>

            <button 
              onClick={() => setStep(3)} 
              disabled={!formData.recipientName || !formData.recipientAddress || !formData.deliveryDate} 
              style={{ width: '100%', padding: '16px', background: (!formData.recipientName || !formData.recipientAddress || !formData.deliveryDate) ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: (!formData.recipientName || !formData.recipientAddress || !formData.deliveryDate) ? 'not-allowed' : 'pointer' }}
            >
              Continue to AI Confirmation
            </button>
          </div>
        )}

        {/* Step 3: AI Confirmation */}
        {step === 3 && (
          <div>
            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Review & Confirm</h2>
            
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ color: '#D7B56D', fontSize: '18px', marginBottom: '16px', fontWeight: 'bold' }}>Order Summary</h3>
              
              <div style={{ marginBottom: '12px' }}>
                <p style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px' }}>Arrangement</p>
                <p style={{ color: '#E6D7C3', fontSize: '16px', fontWeight: 'bold' }}>{formData.selectedFlower?.name}</p>
                <p style={{ color: '#8A7355', fontSize: '14px' }}>{formData.selectedFlower?.vendor}</p>
              </div>

              {formData.selectedChocolate && (
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px' }}>Chocolates</p>
                  <p style={{ color: '#E6D7C3', fontSize: '16px' }}>{formData.selectedChocolate.name}</p>
                </div>
              )}

              <div style={{ marginBottom: '12px', paddingTop: '12px', borderTop: '1px solid rgba(165, 124, 51, 0.3)' }}>
                <p style={{ color: '#A57C33', fontSize: '13px', marginBottom: '4px' }}>Deliver To</p>
                <p style={{ color: '#E6D7C3', fontSize: '16px', fontWeight: 'bold' }}>{formData.recipientName}</p>
                <p style={{ color: '#8A7355', fontSize: '14px' }}>{formData.recipientAddress}</p>
                <p style={{ color: '#A57C33', fontSize: '14px', marginTop: '4px' }}>
                  <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  {formData.deliveryDate}
                </p>
              </div>

              <div style={{ borderTop: '1px solid rgba(165, 124, 51, 0.3)', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: '#A57C33', fontSize: '14px' }}>Subtotal:</span>
                  <span style={{ color: '#E6D7C3', fontSize: '14px' }}>${formData.subtotal}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#A57C33', fontSize: '14px' }}>Near U Service Fee (10%):</span>
                  <span style={{ color: '#E6D7C3', fontSize: '14px' }}>${formData.platformFee}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(165, 124, 51, 0.3)' }}>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '18px' }}>Total:</span>
                  <span style={{ color: '#D7B56D', fontWeight: 'bold', fontSize: '20px' }}>${formData.totalPrice}</span>
                </div>
              </div>
            </div>

            <button 
              onClick={handleAIConfirmation} 
              disabled={loading}
              style={{ width: '100%', padding: '16px', background: loading ? '#6B5635' : 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'AI Concierge Processing...' : 'Confirm with AI Concierge'}
            </button>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && (
          <div>
            {confirmationSent && (
              <div style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '12px', padding: '16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CheckCircle size={24} color="#22c55e" />
                <div>
                  <p style={{ color: '#22c55e', fontWeight: 'bold', marginBottom: '4px' }}>✓ Order Confirmed!</p>
                  <p style={{ color: '#A57C33', fontSize: '14px' }}>Florist has been notified</p>
                </div>
              </div>
            )}

            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
              <h3 style={{ color: '#D7B56D', fontSize: '18px', marginBottom: '12px', fontWeight: 'bold' }}>AI Concierge Confirmation</h3>
              <p style={{ color: '#E6D7C3', lineHeight: '1.6' }}>{aiResponse}</p>
            </div>

            <h2 style={{ fontSize: '24px', color: '#E6D7C3', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>Complete Payment</h2>
            <Elements stripe={stripePromise}>
              <PaymentForm 
                orderData={{
                  deliveryAddress: formData.recipientAddress,
                  deliveryDate: formData.deliveryDate,
                  flowerType: formData.selectedFlower?.name || '',
                  chocolateType: formData.selectedChocolate?.name || 'None',
                  totalPrice: formData.totalPrice,
                  message: formData.message,
                  recipientType: formData.recipientType,
                  recipientMember: formData.recipientMemberId,
                  recipientInfo: formData.recipientName
                }} 
                onSuccess={handlePaymentSuccess} 
              />
            </Elements>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && qrData && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #D4A5A5 0%, #C28A8A 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              <Heart size={40} color="#2C1B13" />
            </div>
            <h2 style={{ fontSize: '28px', color: '#E6D7C3', marginBottom: '12px', fontFamily: 'Playfair Display, serif' }}>Gift Sent!</h2>
            <p style={{ color: '#A57C33', marginBottom: '32px', fontSize: '16px' }}>Luxury delivery scheduled</p>
            <div style={{ background: 'white', padding: '24px', borderRadius: '16px', display: 'inline-block', marginBottom: '24px' }}>
              <QRCodeCanvas id="qr-code-canvas" value={qrData.qr_url} size={250} level="H" />
            </div>
            <div style={{ background: 'rgba(230, 215, 195, 0.05)', border: '1px solid #A57C33', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
              <p style={{ color: '#A57C33', fontSize: '14px', marginBottom: '8px' }}>Gift Details</p>
              <h3 style={{ color: '#E6D7C3', fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{formData.selectedFlower?.name}</h3>
              <p style={{ color: '#D7B56D' }}>{formData.selectedFlower?.vendor}</p>
              {formData.selectedChocolate && <p style={{ color: '#D7B56D' }}>+ {formData.selectedChocolate.name}</p>}
              <p style={{ color: '#E6D7C3', marginTop: '8px' }}>
                <Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Delivery: {qrData.delivery_date}
              </p>
              <p style={{ color: '#8A7355', fontSize: '14px', marginTop: '8px' }}>Code: {qrData.unique_code}</p>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <button onClick={shareQRCode} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)', color: '#2C1B13', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Share2 size={20} /> Share
              </button>
              <button onClick={downloadQRCode} style={{ flex: 1, padding: '14px', background: 'rgba(230, 215, 195, 0.1)', color: '#E6D7C3', border: '1px solid #A57C33', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Download size={20} /> Download
              </button>
            </div>
            <button onClick={() => navigate('/gifts')} style={{ width: '100%', padding: '14px', background: 'none', color: '#A57C33', border: 'none', fontSize: '16px', cursor: 'pointer' }}>Back to Gifts</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendRomancePage;
